package streetlight.model.data

import kampfire.api.TableId
import kampfire.model.Labeled
import kampfire.utils.pascalToTitle
import kotlinx.serialization.Serializable
import kotlin.jvm.JvmInline
import kotlin.time.Duration
import kotlin.time.Duration.Companion.days
import kotlin.time.Duration.Companion.hours
import kotlin.time.Duration.Companion.minutes
import kotlin.time.Instant

/** The site's metrics over one period, at one [MetricResolution]. */
@Serializable
data class SiteStatus(
    val siteStatusId: SiteStatusId,
    val integers: Map<SiteMetric, Int>,
    val doubles: Map<SiteMetric, Double>,
    val resolution: MetricResolution,
    val coverage: Float,
    val startedAt: Instant,
    val endedAt: Instant,
    val createdAt: Instant,
) {
    /** The value of [metric] in this period, or `null` when it was not recorded. */
    fun getMetricOrNull(metric: SiteMetric): Double? = when(metric.metricType) {
        MetricType.Count -> integers[metric]?.toDouble()
        MetricType.Average -> doubles[metric]
        MetricType.Max -> doubles[metric]
    }
    fun getMetricOrZero(metric: SiteMetric) = getMetricOrNull(metric) ?: 0.0
}

@Serializable
@JvmInline
value class SiteStatusId(override val value: Long): TableId<Long> {
    companion object {
        val Empty = SiteStatusId(0L)
    }
}

/** The metrics the site records, each read from a Micrometer meter. */
enum class SiteMetric(
    val metricType: MetricType,
    val meterName: String,
    val metricAxis: MetricAxis? = null,
): Labeled {
    RequestCount(MetricType.Count, "ktor.http.server.requests"),
    ResponseLatencyAverage(MetricType.Average, "ktor.http.server.requests", MetricAxis.RequestLatency),
    ResponseLatencyMax(MetricType.Max, "ktor.http.server.requests", MetricAxis.RequestLatency);

    override val label = name.pascalToTitle()
}

/** How a metric is gathered over a period. */
enum class MetricType {
    Count,
    Average,
    Max,
}

/** The axis a metric is charted on, shared by metrics of the same unit. */
enum class MetricAxis: Labeled {
    RequestLatency;

    override val label = name.pascalToTitle()
}

/** The periods metrics are recorded over. Each is aggregated from the one before it. */
enum class MetricResolution(val duration: Duration) {
    OneMinute(1.minutes),
    FiveMinutes(5.minutes),
    ThirtyMinutes(30.minutes),
    ThreeHours(3.hours),
    OneDay(24.hours),
    OneWeek(7.days);

    val predecessor: MetricResolution?
        get() = if (ordinal > 0) entries[ordinal - 1] else null
}

/** A labeled moment on a chart. */
data class ChartEvent(
    val label: String,
    val time: Instant,
)